import {
  SyncQueue,
  type BatchItemResult,
  type BatchResponse,
  type FetchImpl,
  type OutboxEntry,
} from "@norkron/web-sync-queue";
import { api } from "../api";
import { ApiError } from "../api/types";
import type {
  Comment,
  CommentCreate,
  ReactionGroup,
  ReplyCreate,
} from "../api/types";
import type { CommentEditResponse } from "../api/types";
import { buildOutboxStore, HEARTBEAT_ENDPOINT } from "./queue";
import { OutboxTable, reactionToggleKey } from "./mutations";

/**
 * The REST-replay outbox (offline-sync design, extended to reactions + comment
 * body writes). A SECOND durable {@link SyncQueue} — its own db/table, distinct
 * from the batch outbox — that drains through the web-sync-queue **custom
 * transport seam** instead of `POST /api/sync/batch`. Each queued entry replays
 * through the mutation's OWN already-idempotent adapter method:
 *
 *   · `reactions`       → api.toggleReaction  (PUT/DELETE /comments/:cid/reactions/:emoji)
 *   · `comment_edit`    → api.editComment     (PATCH      /comments/:cid)
 *   · `comment_create`  → api.createComment / api.replyToComment (POST)
 *
 * It replays through the SAME `api` adapter the app uses (real or mock), so the
 * CSRF double-submit + one-shot stale-token refresh live in ONE place
 * (`realAdapter.request`) and mock-mode dev keeps working. The endpoints exist +
 * are idempotent on the DEPLOYED backend, so a queued mutation persists on
 * reconnect with NO backend change — unlike the batch terminal, which does not
 * (yet) dispatch a `reactions`/`comment_edit` table. The engine (web-sync-queue)
 * owns durability, FIFO order, backoff, idempotency (UUID-v7 `client_event_uuid`),
 * and cold-start replay; this module owns only the elaborate glue: the per-table
 * sender + the reconcile bus screens fold into state.
 */

const REST_OUTBOX_DB = "elaborate-rest-outbox";
const REST_OUTBOX_TABLE = "rest_outbox";

// ── Reconcile bus: server truth folded back into React state ──────────────────

export type RestReconcile =
  | { table: "reactions"; kind: "applied"; commentId: number; echo: ReactionGroup }
  | {
      table: "reactions";
      kind: "rejected";
      commentId: number;
      emoji: string;
      add: boolean;
      message: string;
    }
  | { table: "comment_edit"; kind: "applied"; commentId: number; row: CommentEditResponse }
  | { table: "comment_edit"; kind: "rejected"; commentId: number; message: string }
  | { table: "comment_create"; kind: "applied"; tempId: string; comment: Comment }
  | { table: "comment_create"; kind: "rejected"; tempId: string; message: string };

export type RestReconcileListener = (e: RestReconcile) => void;

// ── Payload shapes (the durable JSON the transport re-reads at drain time) ────

interface ReactionPayload {
  comment_id: number;
  emoji: string;
  add: boolean;
}
interface CommentEditPayload {
  comment_id: number;
  body: string;
  body_format: string;
}
interface CommentCreatePayload {
  __tempId: string;
  document_id: number | string;
  /** A real parent id ⇒ a reply POST; absent ⇒ a root create POST. */
  parent_id?: number;
  draft: CommentCreate | ReplyCreate;
}

/**
 * How a thrown {@link ApiError} maps onto the outbox lifecycle:
 *   · `null` (transient — offline / network / 429 / 5xx) → the engine re-pends +
 *     backs off; the optimistic UI stands and the "queued" affordance shows.
 *   · a message string (a GENUINE 4xx: author-403, validation) → a `rejected`
 *     verdict that rolls the optimistic patch back with a specific reason.
 */
function classify(err: unknown, fallback: string): string | null {
  if (err instanceof ApiError) {
    if (err.status === 0 || err.status === 429 || err.status >= 500) return null;
    if (err.status === 409) return null; // let a fresh reload settle a conflict
    return err.message || fallback;
  }
  // An unknown throw is treated as transient — a replay is always safe (idempotent).
  return null;
}

const applied = (uuid: string, row?: unknown): BatchItemResult => ({
  client_event_uuid: uuid,
  status: "applied",
  server_row: row,
});
const rejectedResult = (uuid: string, message: string): BatchItemResult => ({
  client_event_uuid: uuid,
  status: "rejected",
  message,
});

/** Minimal credentials fetch for the connectivity heartbeat (HEAD /api/health). */
const heartbeatFetch: FetchImpl = (input, init) =>
  fetch(input, { ...init, credentials: "include" });

export class RestOutbox {
  readonly queue: SyncQueue;
  private readonly listeners = new Set<RestReconcileListener>();
  private readonly progressListeners = new Set<() => void>();
  private started = false;

  constructor(store = buildOutboxStore(REST_OUTBOX_DB, REST_OUTBOX_TABLE)) {
    this.queue = new SyncQueue({
      // Documentary sink — the transport replaces the batch POST entirely.
      endpoint: "/api/comments/reactions",
      store,
      outboxTable: REST_OUTBOX_TABLE,
      heartbeatUrl: HEARTBEAT_ENDPOINT,
      fetchImpl: heartbeatFetch,
      transport: (entries) => this.drain(entries),
      onProgress: () => {
        for (const l of this.progressListeners) l();
      },
    });
  }

  subscribe(l: RestReconcileListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  onProgress(l: () => void): () => void {
    this.progressListeners.add(l);
    return () => this.progressListeners.delete(l);
  }
  private emit(e: RestReconcile): void {
    for (const l of this.listeners) l(e);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.queue.start();
  }
  stop(): void {
    this.started = false;
    this.queue.stop();
  }
  get isOnline(): boolean {
    return this.queue.isOnline;
  }

  /** The (cid:emoji / edit:cid / create:tempId) keys currently unsynced. */
  async pendingKeys(): Promise<Set<string>> {
    const rows = await this.queue.listPending();
    const s = new Set<string>();
    for (const r of rows) s.add(pendingKeyOf(r));
    return s;
  }

  // ── enqueue helpers (the optimistic UI patch is the caller's job) ───────────

  enqueueReaction(
    userId: number,
    commentId: number,
    emoji: string,
    add: boolean,
  ): Promise<number> {
    const payload: ReactionPayload = { comment_id: commentId, emoji, add };
    return this.queue.enqueue({
      table: OutboxTable.reactions,
      operation: "update",
      payload,
      clientEventUuid: reactionToggleKey(userId, commentId, emoji, add),
    });
  }

  enqueueEdit(commentId: number, body: string, body_format: string): Promise<number> {
    const payload: CommentEditPayload = { comment_id: commentId, body, body_format };
    return this.queue.enqueue({ table: "comment_edit", operation: "update", payload });
  }

  enqueueCreateRoot(
    tempId: string,
    documentId: number | string,
    draft: CommentCreate,
  ): Promise<number> {
    const payload: CommentCreatePayload = {
      __tempId: tempId,
      document_id: documentId,
      draft,
    };
    return this.queue.enqueue({ table: "comment_create", operation: "create", payload });
  }

  enqueueCreateReply(
    tempId: string,
    parentId: number,
    documentId: number | string,
    draft: ReplyCreate,
  ): Promise<number> {
    const payload: CommentCreatePayload = {
      __tempId: tempId,
      document_id: documentId,
      parent_id: parentId,
      draft,
    };
    return this.queue.enqueue({ table: "comment_create", operation: "create", payload });
  }

  // ── the custom transport: replay each entry via its own adapter method ──────

  private async drain(entries: readonly OutboxEntry[]): Promise<BatchResponse> {
    const results: BatchItemResult[] = [];
    for (const e of entries) {
      const r = await this.replayOne(e);
      // `null` ⇒ transient: OMIT so the engine re-pends + retries with backoff.
      if (r) results.push(r);
    }
    // Reclaim confirmed rows so the pending set is exactly the unsynced set.
    void this.queue.pruneSucceeded();
    return { results };
  }

  private async replayOne(e: OutboxEntry): Promise<BatchItemResult | null> {
    if (e.table === OutboxTable.reactions) return this.replayReaction(e);
    if (e.table === "comment_edit") return this.replayEdit(e);
    if (e.table === "comment_create") return this.replayCreate(e);
    return rejectedResult(e.client_event_uuid, `unknown table ${e.table}`);
  }

  private async replayReaction(e: OutboxEntry): Promise<BatchItemResult | null> {
    const p = e.payload as ReactionPayload;
    try {
      const res = await api.toggleReaction(p.comment_id, p.emoji, p.add);
      this.emit({
        table: "reactions",
        kind: "applied",
        commentId: p.comment_id,
        echo: res.reaction,
      });
      return applied(e.client_event_uuid, res.reaction);
    } catch (err) {
      const message = classify(err, "Couldn't update reaction");
      if (message === null) return null; // transient
      this.emit({
        table: "reactions",
        kind: "rejected",
        commentId: p.comment_id,
        emoji: p.emoji,
        add: p.add,
        message,
      });
      return rejectedResult(e.client_event_uuid, message);
    }
  }

  private async replayEdit(e: OutboxEntry): Promise<BatchItemResult | null> {
    const p = e.payload as CommentEditPayload;
    try {
      const row = await api.editComment(p.comment_id, {
        body: p.body,
        body_format: p.body_format,
      });
      this.emit({ table: "comment_edit", kind: "applied", commentId: p.comment_id, row });
      return applied(e.client_event_uuid, row);
    } catch (err) {
      const message = classify(
        err,
        err instanceof ApiError && err.status === 403
          ? "Only the author can edit this comment"
          : "Couldn't save the edit",
      );
      if (message === null) return null;
      this.emit({ table: "comment_edit", kind: "rejected", commentId: p.comment_id, message });
      return rejectedResult(e.client_event_uuid, message);
    }
  }

  private async replayCreate(e: OutboxEntry): Promise<BatchItemResult | null> {
    const p = e.payload as CommentCreatePayload;
    try {
      const comment =
        p.parent_id != null
          ? await api.replyToComment(p.parent_id, p.draft as ReplyCreate)
          : await api.createComment(String(p.document_id), p.draft as CommentCreate);
      this.emit({ table: "comment_create", kind: "applied", tempId: p.__tempId, comment });
      return applied(e.client_event_uuid, comment);
    } catch (err) {
      const message = classify(err, "Couldn't save the comment");
      if (message === null) return null;
      this.emit({ table: "comment_create", kind: "rejected", tempId: p.__tempId, message });
      return rejectedResult(e.client_event_uuid, message);
    }
  }
}

/** The unsynced-key for a pending entry (drives the per-item "queued" affordance). */
export function pendingKeyOf(entry: OutboxEntry): string {
  if (entry.table === OutboxTable.reactions) {
    const p = entry.payload as ReactionPayload;
    return `reaction:${p.comment_id}:${p.emoji}`;
  }
  if (entry.table === "comment_edit") {
    const p = entry.payload as CommentEditPayload;
    return `edit:${p.comment_id}`;
  }
  const p = entry.payload as CommentCreatePayload;
  return `create:${p.__tempId}`;
}

// ── process-wide singleton (started by SyncProvider) ─────────────────────────

let g_rest: RestOutbox | null = null;

export function getRestOutbox(): RestOutbox {
  if (!g_rest) g_rest = new RestOutbox();
  return g_rest;
}
export function startRestOutbox(): void {
  getRestOutbox().start();
}
export function stopRestOutbox(): void {
  g_rest?.stop();
}
export function __resetRestOutboxForTests(): void {
  g_rest?.stop();
  g_rest = null;
}
